import React, { useEffect } from 'react';
import { Box, Typography, TableRow, TableCell, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchLotteries } from 'store/actions/lotteryActions';
import { usePermissions } from 'hooks/usePermissions';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import { LoadingStatus } from 'store/reducers/lotteryReducer';
import { PageHeader } from 'components/Layout/PageHeader';
import { CustomMenu, type CustomMenuRow } from 'components/CustomMenu/CustomMenu';
import { LotteryInfoPopover } from 'components/LotteryInfoPopover/LotteryInfoPopover';
import { CellTrigger } from 'components/StyledTable/CellTrigger';
import type { ILottery } from 'types/ILottery';

const columns: SortableColumn<ILottery>[] = [
  { name: 'Name (Abk.)', width: '35%', sortBy: (lottery) => lottery.nameShort },
  { name: 'Name', width: '45%', sortBy: (lottery) => lottery.name },
  { name: 'Aktionen', width: '1%' },
];

export const LotteryListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteries, loading } = useAppSelector((state) => state.lotteries);
  const { rows: sortedLotteries, columns: tableColumns, sort } = useTableSort(columns, lotteries);
  const user = useAppSelector((state) => state.auth.user);
  const { isSuperAdmin } = usePermissions();
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [menuLottery, setMenuLottery] = React.useState<ILottery | null>(null);
  const [infoAnchor, setInfoAnchor] = React.useState<HTMLElement | null>(null);
  const [infoLottery, setInfoLottery] = React.useState<ILottery | null>(null);

  const openInfo = (lottery: ILottery) => (anchor: HTMLElement) => {
    setInfoAnchor(anchor);
    setInfoLottery(lottery);
  };

  useEffect(() => {
    if (!isSuperAdmin() && user?.lotteryId) {
      navigate(`/lottery/${user.lotteryId}/drawings`, { replace: true });
      return;
    }
  }, [navigate, isSuperAdmin, user?.lotteryId]);

  useEffect(() => {
    if(loading === LoadingStatus.IDLE) {
      dispatch(fetchLotteries());
    }
  }, [dispatch, navigate, loading]);

  const buildMenuItems = (): CustomMenuRow[] => {
    if (!menuLottery) return [];

    return [{ name: 'Benutzerverwaltung', onClick: () => navigate(`/lottery/${menuLottery.id}/users`) }];
  };

  return (
    <Box>
      <PageHeader title="Lotteriebetreiber" />

      {!loading && lotteries.length === 0 && (
        <Typography variant="body1" sx={{ color: '#969696' }}>
          Keine Lotteriebetreiber vorhanden.
        </Typography>
      )}

      {lotteries.length > 0 && (
        <StyledTable columns={tableColumns} sort={sort}>
          {sortedLotteries.map((lottery, index) => (
            <TableRow key={lottery.id} className={`psf-styled-table__body-row ${index === sortedLotteries.length - 1 ? 'psf-styled-table__body-last-row' : ''}`}>
              <TableCell>
                <CellTrigger onOpen={openInfo(lottery)}>{lottery.nameShort}</CellTrigger>
              </TableCell>
              <TableCell>
                <CellTrigger onOpen={openInfo(lottery)}>{lottery.name}</CellTrigger>
              </TableCell>
              <TableCell>
                <Box className="psf-styled-table__body-cell-action-container">
                  <IconButton
                    className="psf-styled-table__body-cell-action-button"
                    aria-label="Aktionen"
                    onClick={(event) => {
                      setMenuAnchor(event.currentTarget);
                      setMenuLottery(lottery);
                    }}
                  >
                    <MoreVertIcon className="psf-styled-table__body-cell-action-button-icon" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </StyledTable>
      )}

      <CustomMenu
        anchorEl={menuAnchor}
        handleMenuClose={() => {
          setMenuAnchor(null);
          setMenuLottery(null);
        }}
        withDivider
        isArrowRight
        items={buildMenuItems()}
      />

      <LotteryInfoPopover
        lottery={infoLottery}
        anchorEl={infoAnchor}
        onClose={() => {
          setInfoAnchor(null);
          setInfoLottery(null);
        }}
      />
    </Box>
  );
};
